import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/apiService';
import { Recording, ProcessingStatus } from '../types';
import { RECORDER_COMPLETE_EVENT, RECORDING_UPDATED_EVENT } from '../utils/recorderEvents';
import { useFeature } from '../hooks/useFeature';
import { logger } from '../utils/logger';

const SIDEBAR_COLLAPSED_KEY = 'notizy.sidebarCollapsed';
const MAX_RECENT_ITEMS = 7;

/** Audiowave-logo zoals in het Redesign 2026-ontwerp */
const WaveformLogo: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 38 32" fill="currentColor" aria-hidden="true">
        <rect y="13.8947" width="0.842105" height="2.52632" rx="0.421053" />
        <rect x="1.68408" y="11.3684" width="0.842105" height="7.57895" rx="0.421053" />
        <rect x="3.36865" y="7.57892" width="0.842105" height="15.1579" rx="0.421053" />
        <rect x="5.05273" y="8.8421" width="0.842105" height="12.6316" rx="0.421053" />
        <rect x="6.73682" y="12.2105" width="0.842105" height="5.89474" rx="0.421053" />
        <rect x="8.4209" y="10.1053" width="0.842105" height="10.1053" rx="0.421053" />
        <rect x="10.1055" y="1.26318" width="0.842105" height="27.7895" rx="0.421053" />
        <rect x="11.7896" width="0.842105" height="30.3158" rx="0.421053" />
        <rect x="13.4736" width="0.842105" height="30.3158" rx="0.421053" />
        <rect x="15.1577" y="1.6842" width="0.842105" height="12.6316" rx="0.421053" />
        <rect x="16.8423" y="4.21051" width="0.842105" height="12.6316" rx="0.421053" />
        <rect x="18.5264" y="6.73682" width="0.842105" height="12.6316" rx="0.421053" />
        <rect x="20.2104" y="9.26318" width="0.842105" height="12.6316" rx="0.421053" />
        <rect x="21.8945" y="11.7895" width="0.842105" height="12.6316" rx="0.421053" />
        <rect x="23.5791" y="14.3158" width="0.842105" height="12.6316" rx="0.421053" />
        <rect x="25.2632" y="16.8421" width="0.842105" height="12.6316" rx="0.421053" />
        <rect x="37.0527" y="13.8947" width="0.842105" height="2.52632" rx="0.421053" />
        <rect x="35.3687" y="8.8421" width="0.842105" height="12.6316" rx="0.421053" />
        <rect width="0.842105" height="5.89474" rx="0.421053" transform="matrix(-1 0 0 1 34.5264 12.2105)" />
        <rect width="0.842105" height="10.1053" rx="0.421053" transform="matrix(-1 0 0 1 32.8423 10.1053)" />
        <rect width="0.842105" height="27.7895" rx="0.421053" transform="matrix(-1 0 0 1 31.1577 1.26318)" />
        <rect width="0.842105" height="30.3158" rx="0.421053" transform="matrix(-1 0 0 1 29.4736 0)" />
        <rect width="0.842105" height="30.3158" rx="0.421053" transform="matrix(-1 0 0 1 27.7896 0)" />
    </svg>
);

const formatRecentDate = (value?: string): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    const time = date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    return `${day}, ${time}`;
};

interface NavItemProps {
    icon: React.ReactNode;
    label: string;
    active?: boolean;
    collapsed?: boolean;
    onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, active, collapsed, onClick }) => (
    <button
        onClick={onClick}
        title={collapsed ? label : undefined}
        className={`flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-sm font-medium transition-all duration-200 ${active
            ? 'bg-white/[0.12] font-semibold text-[#FAF7F3]'
            : 'text-[#FAF7F3]/[0.68] hover:bg-white/[0.06] hover:text-[#FAF7F3]'
            } ${collapsed ? 'justify-center px-0' : ''}`}
    >
        <span className="h-[18px] w-[18px] flex-none opacity-80">{icon}</span>
        {!collapsed && <span className="truncate">{label}</span>}
    </button>
);

const Sidebar: React.FC<{
    collapsed: boolean;
    onToggleCollapsed: () => void;
    onNavigate?: () => void;
}> = ({ collapsed, onToggleCollapsed, onNavigate }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const apiService = useMemo(() => new ApiService(), []);

    const [recent, setRecent] = useState<Recording[]>([]);
    const [isLoadingRecent, setIsLoadingRecent] = useState(false);

    const loadRecent = useCallback(async () => {
        if (!user) return;
        setIsLoadingRecent(true);
        try {
            const data = await apiService.getRecordings();
            setRecent(data.slice(0, MAX_RECENT_ITEMS));
        } catch (err) {
            logger.error('Fout bij laden recente gesprekken:', err);
        } finally {
            setIsLoadingRecent(false);
        }
    }, [apiService, user]);

    useEffect(() => {
        loadRecent();
    }, [loadRecent]);

    useEffect(() => {
        const handleComplete = () => loadRecent();
        window.addEventListener(RECORDER_COMPLETE_EVENT, handleComplete);
        window.addEventListener(RECORDING_UPDATED_EVENT, handleComplete);
        return () => {
            window.removeEventListener(RECORDER_COMPLETE_EVENT, handleComplete);
            window.removeEventListener(RECORDING_UPDATED_EVENT, handleComplete);
        };
    }, [loadRecent]);

    const go = (path: string) => {
        navigate(path);
        onNavigate?.();
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const showTemplates = useFeature('template_editing');
    const onRecordings = location.pathname.startsWith('/recording');

    return (
        <nav
            className={`flex h-full flex-col bg-sidebar text-[#FAF7F3] transition-[width] duration-200 ${collapsed ? 'w-[74px] px-2.5' : 'w-60 px-4'} pt-7 pb-5`}
            aria-label={t('appShell.nav.ariaLabel')}
        >
            {/* Logo + inklap-toggle */}
            <div className={`mb-5 flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-1`}>
                <button
                    onClick={() => go('/recordings')}
                    className="flex items-center gap-2 text-white transition-opacity hover:opacity-85"
                >
                    <WaveformLogo className="h-6 w-7 flex-none" />
                    {!collapsed && (
                        <span className="notizy-wordmark">notizy</span>
                    )}
                </button>
                {!collapsed && (
                    <button
                        onClick={onToggleCollapsed}
                        className="hidden rounded-md p-1.5 text-[#FAF7F3]/[0.48] transition-colors hover:bg-white/[0.06] hover:text-[#FAF7F3] md:block"
                        title={t('appShell.sidebar.collapse')}
                    >
                        <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="16" rx="2" />
                            <path d="M9 4v16" />
                        </svg>
                    </button>
                )}
            </div>

            {collapsed && (
                <button
                    onClick={onToggleCollapsed}
                    className="mb-3 hidden self-center rounded-md p-1.5 text-[#FAF7F3]/[0.48] transition-colors hover:bg-white/[0.06] hover:text-[#FAF7F3] md:block"
                    title={t('appShell.sidebar.expand')}
                >
                    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="16" rx="2" />
                        <path d="M9 4v16" />
                    </svg>
                </button>
            )}

            <NavItem
                label={t('appShell.nav.recordings')}
                active={onRecordings}
                collapsed={collapsed}
                onClick={() => go('/recordings')}
                icon={
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3z" />
                        <path d="M19 11a7 7 0 0 1-14 0M12 18v3" />
                    </svg>
                }
            />

            {/* Recente gesprekken */}
            {!collapsed && (
                <div className="mt-5 flex min-h-0 flex-1 flex-col">
                    <div className="mb-1 flex items-center justify-between px-3">
                        <span className="text-xs font-semibold text-[#FAF7F3]/[0.68]">{t('appShell.sidebar.recentConversations')}</span>
                        <button
                            onClick={loadRecent}
                            className="rounded p-1 text-[#FAF7F3]/[0.48] transition-colors hover:bg-white/[0.06] hover:text-[#FAF7F3]"
                            title={t('appShell.sidebar.refresh')}
                        >
                            <svg className={`h-3.5 w-3.5 ${isLoadingRecent ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 12a9 9 0 1 1-2.64-6.36M21 4v5h-5" />
                            </svg>
                        </button>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {recent.map((recording) => {
                            const processed = recording.status === ProcessingStatus.PROCESSED;
                            const activeItem = location.pathname === `/recording/${recording.id}`;
                            return (
                                <button
                                    key={recording.id}
                                    onClick={() => processed && go(`/recording/${recording.id}`)}
                                    className={`block w-full rounded-[10px] px-3 py-2 text-left transition-colors ${activeItem
                                        ? 'bg-white/[0.12]'
                                        : processed
                                            ? 'hover:bg-white/[0.06]'
                                            : 'cursor-default opacity-60'
                                        }`}
                                >
                                    <span className="block truncate text-sm text-[#FAF7F3]/[0.88]">
                                        {recording.name || t('appShell.sidebar.untitledRecording')}
                                    </span>
                                    <span className="block text-xs text-[#FAF7F3]/[0.48]">
                                        {formatRecentDate(recording.recordingDateTime ?? recording.createdAt)}
                                    </span>
                                </button>
                            );
                        })}
                        <button
                            onClick={() => go('/recordings')}
                            className="flex w-full items-center gap-1.5 rounded-[10px] px-3 py-2 text-left text-sm text-[#FAF7F3]/[0.68] transition-colors hover:bg-white/[0.06] hover:text-[#FAF7F3]"
                        >
                            <span className="truncate">{t('appShell.sidebar.more')}</span>
                            <svg className="h-3.5 w-3.5 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
            {collapsed && <div className="flex-1" />}

            {/* Onderin: secundaire navigatie */}
            <div className="mt-4 border-t border-white/[0.08] pt-3">
                {showTemplates && (
                    <NavItem
                        label={t('appShell.nav.templates')}
                        active={location.pathname.startsWith('/templates')}
                        collapsed={collapsed}
                        onClick={() => go('/templates')}
                        icon={
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                <path d="M14 3v6h6M9 13h6M9 17h6" />
                            </svg>
                        }
                    />
                )}
                <NavItem
                    label={t('appShell.nav.updates')}
                    active={location.pathname.startsWith('/updates')}
                    collapsed={collapsed}
                    onClick={() => go('/updates')}
                    icon={
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
                        </svg>
                    }
                />
                <NavItem
                    label={t('appShell.nav.account')}
                    active={location.pathname === '/user-settings'}
                    collapsed={collapsed}
                    onClick={() => go('/user-settings')}
                    icon={
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                    }
                />
                <NavItem
                    label={t('appShell.nav.logout')}
                    collapsed={collapsed}
                    onClick={handleLogout}
                    icon={
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                        </svg>
                    }
                />
            </div>
        </nav>
    );
};

/**
 * AppShell — Redesign 2026: donkergroen frame rond het venster met de
 * sidebar als onderdeel van het frame en de content als lichte,
 * afgeronde plaat (zie redesign/styleguide.html, sectie 4).
 */
const AppShell: React.FC<{ children: React.ReactNode; banner?: React.ReactNode }> = ({ children, banner }) => {
    const { t } = useTranslation();
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
        } catch {
            return false;
        }
    });
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const toggleCollapsed = () => {
        setCollapsed(prev => {
            const next = !prev;
            try {
                localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
            } catch {
                // localStorage niet beschikbaar — niet persisteren
            }
            return next;
        });
    };

    return (
        <div className="flex h-screen bg-sidebar p-2">
            {/* Sidebar: vast op desktop, drawer op mobiel */}
            <div className="hidden md:block">
                <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
            </div>

            {mobileOpen && (
                <div className="fixed inset-0 z-40 md:hidden">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setMobileOpen(false)}
                        aria-hidden="true"
                    />
                    <div className="absolute inset-y-0 left-0 bg-sidebar shadow-hard">
                        <Sidebar
                            collapsed={false}
                            onToggleCollapsed={() => undefined}
                            onNavigate={() => setMobileOpen(false)}
                        />
                    </div>
                </div>
            )}

            <main
                className="relative min-w-0 flex-1 overflow-y-auto rounded-lg bg-background"
                style={{
                    borderRadius: 'var(--app-shell-radius)',
                    // Half the sidebar width — lets fixed-position overlays (e.g. the
                    // streaming indicator) center over the content area, not the window.
                    ['--content-center-shift' as string]: collapsed ? '37px' : '120px',
                } as React.CSSProperties}
            >
                {banner && (
                    <div className="sticky top-0 z-50">{banner}</div>
                )}
                {/* Mobiele menu-knop */}
                <button
                    onClick={() => setMobileOpen(true)}
                    className="fixed bottom-5 left-5 z-30 rounded-full bg-sidebar p-2.5 text-white shadow-medium md:hidden"
                    title={t('appShell.openMenu')}
                >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
                {children}
            </main>
        </div>
    );
};

export default AppShell;
